import SwitchActivePair from '@/components/common/SwitchActivePair';
import ModuleWindow, { moduleHeaderClassName } from '@/components/mosaic/ModuleWindow';
import ModuleWindowControls from '@/components/mosaic/ModuleWindowControl';

import { switchOrderBook } from '@/services/MarketService';
import { useAppSelector } from '@/state/hooks';
import { selectAccountType } from '@/state/reducers/authSlice';
import { selectActiveMarketPair, selectMarketPairs } from '@/state/reducers/marketPairSlice';
import { selectMosaic } from '@/state/reducers/mosaicSlice';
import { selectInstrumentConfigs } from '@/state/reducers/ohoSlice';
import { getInstrumentConfig } from '@/utils/hooks/useInstrument';
import useLanguage from '@/utils/hooks/useLanguage';
import { tabIcon } from '@/utils/hooks/useXplorSpotLayout';
import cn from 'classnames';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { isTablet } from 'react-device-detect';
import { MdOpenInNew } from 'react-icons/md';
import AdvancedChartView from './AdvancedChartView';

const ChartModule = (props: any) => {
    const { screen, tradingModule } = props;

    const lang = useLanguage();
    const mosaic = useAppSelector(selectMosaic);
    const pairs = useAppSelector(selectMarketPairs);
    const activePair = useAppSelector(selectActiveMarketPair);
    const accountType = useAppSelector(selectAccountType);
    const instrumentConfigs = useAppSelector(selectInstrumentConfigs);

    const onPopOut = useCallback(
        () => window.open(`/chart?symbol=${activePair?.netdania}`, '', 'width=1200,height=600'),
        [activePair]
    );

    const instrumentOptions = useMemo(() => {
        return pairs.map((instrument) => {
            const config: any = getInstrumentConfig(accountType, instrumentConfigs, instrument.celer);

            return {
                label: instrument.show,
                value: instrument.celer,
                market: instrument,
                config
            };
        });
    }, [pairs, instrumentConfigs, accountType]);

    const [isMouseDown, setIsMouseDown] = useState<any>(false);

    useEffect(() => {
        const handleMouseDown = (event) => {
            if (event.button === 0) setIsMouseDown(true);
        };

        const handleMouseUp = (event) => {
            if (event.button === 0) setIsMouseDown(false);
        };

        document.querySelectorAll('.mosaic-split').forEach((element) => {
            element.addEventListener('mousedown', handleMouseDown);
            element.addEventListener('mouseup', handleMouseUp);
        });

        window.addEventListener('mouseup', handleMouseUp);

        // Cleanup: Remove event listeners when the component is unmounted
        return () => {
            document.querySelectorAll('.mosaic-split').forEach((element) => {
                element.removeEventListener('mousedown', handleMouseDown);
                element.removeEventListener('mouseup', handleMouseUp);
            });
            window.removeEventListener('mouseup', handleMouseUp);
        };
    }, [isMouseDown]);

    const shouldOverlayCharts = useMemo(
        () => isMouseDown || mosaic.isDragging || mosaic.isResizing,
        [isMouseDown, mosaic]
    );

    return (
        <ModuleWindow
            {...props}
            renderToolbar={() => (
                <div className={moduleHeaderClassName} dir={lang.dir}>
                    <div className="h-full flex items-center p-2 gap-2">
                        {tabIcon[tradingModule?.title || ''] && (
                            <div className="flex items-center justify-center bg-brand-neutral-700 rounded-full p-1">
                                {tabIcon[tradingModule?.title || '']({
                                    className: 'w-4 h-4',
                                    'aria-hidden': true
                                })}
                            </div>
                        )}
                        <span className="font-semibold">{tradingModule.title}</span>

                        {(screen.laptop || screen.desktop) && !isTablet && (
                            <div
                                title="Open in Separate Window"
                                className={cn('p-2 rounded-sm flex items-center', {
                                    ['cursor-pointer hover:bg-brand-primary']: true
                                })}
                                onClick={onPopOut}>
                                <MdOpenInNew className="w-4 h-4" />
                            </div>
                        )}
                    </div>
                    <div className="flex h-full items-center">
                        {(screen.laptop || screen.desktop) && !isTablet && (
                            <SwitchActivePair
                                selected={{
                                    label: activePair?.show || lang.tickers.instrumentSelector,
                                    value: activePair?.celer || undefined,
                                    market: undefined
                                }}
                                options={instrumentOptions}
                                onSubmit={(instrument) => switchOrderBook(instrument)}
                            />
                        )}
                        <ModuleWindowControls screen={screen} />
                    </div>
                </div>
            )}>
            <div className="h-full bg-brand-background">
                {shouldOverlayCharts && <div className="overlay h-full w-full absolute z-10" />}
                {activePair?.celer && <AdvancedChartView />}
            </div>
        </ModuleWindow>
    );
};

export default ChartModule;
